import { CommonModule, TitleCasePipe } from '@angular/common';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { IconComponent, CobaltButtonModule } from '@msi/cobalt-prime';
import { ConfirmationService } from 'primeng/api';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TemplateConstants } from '@constants/template-constants';
import { ContactInputModel } from '@premise-hazard-models/contact-input.model';
import { DeviceInputModel } from '@premise-hazard-models/device-input.model';
import { DeviceType } from '@premise-hazard-models/device-type.model';

export type ContactFormDialogData = void;

@Component({
  selector: 'app-contact-form-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslocoDirective,
    TranslocoPipe,
    IconComponent,
    CobaltButtonModule,
    InputTextModule,
    SelectModule,
    TitleCasePipe,
  ],
  templateUrl: './contact-form-dialog.component.html',
})
export class ContactFormDialogComponent {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly transloco = inject(TranslocoService);
  private readonly cdr = inject(ChangeDetectorRef);

  public readonly DEVICE_TYPES = TemplateConstants.DeviceTypes;
  public ContactForm: FormGroup = new FormGroup({
    name: new FormControl<string>('', [Validators.required, Validators.minLength(1), Validators.maxLength(255)]),
    devices: new FormArray([]),
  });

  public addDevice(): void {
    const deviceArray = this.ContactForm.controls['devices'] as FormArray;
    deviceArray.push(new FormGroup({
      address: new FormControl<string>('', [Validators.required, Validators.minLength(1), Validators.maxLength(255)]),
      deviceType: new FormControl<DeviceType>(DeviceType.phone, [Validators.required]),
      id: new FormControl<string | null>(null),
    }));
    this.cdr.detectChanges();
  }

  public removeDevice(index: number): void {
    const deviceArray = this.ContactForm.controls['devices'] as FormArray;
    if (index < 0 || index >= deviceArray.length) {
      return;
    }
    this.confirmationService.confirm({
      header: this.transloco.translate('PremiseHazard.Item.modal.removeDevice.header'),
      message: this.transloco.translate('PremiseHazard.Item.modal.removeDevice.content'),
      acceptIcon: 'ic_trash',
      rejectIcon: 'ic_remove',
      acceptLabel: this.transloco.translate('Buttons.remove'),
      rejectLabel: this.transloco.translate('Buttons.cancel'),
      accept: () => {
        deviceArray.removeAt(index);
        this.ContactForm.updateValueAndValidity();
        this.cdr.detectChanges();
      },
    });
  }

  public submit(): void {
    if (!this.ContactForm.valid) {
      return;
    }
    const contact = this.ContactForm.value;
    const devicesToAdd: DeviceInputModel[] = (contact.devices || []).map((d: any) => ({
      address: d.address,
      deviceType: d.deviceType,
    }));
    const result: ContactInputModel = {
      name: contact.name,
      devices: devicesToAdd,
    };
    this.dialogRef.close(result);
  }

  public cancel(): void {
    this.dialogRef.close(null);
  }

  public trackByDeviceIndex(index: number, _device: any): number {
    return index;
  }
}
